import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import EggVisual from "@/components/EggVisual";
import { SocialLinkRow } from "@/components/SocialLinks";
import TipJar from "@/components/TipJar";
import TopTippers from "@/components/TopTippers";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ChefProfile — /chief-cook/:slug
 * Public author page for partner cooks (David Lauer, etc.).
 * Shows: hero with their signature partner-egg, bio, follow button, recipe grid.
 */
export default function ChefProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isAuthed = !!user;
  const [chef, setChef] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [following, setFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: a }, { data: b }] = await Promise.all([
          axios.get(`${API}/chefs/${slug}`),
          axios.get(`${API}/chefs/${slug}/recipes`),
        ]);
        if (cancelled) return;
        setChef(a?.chef || null);
        setRecipes(b?.recipes || []);
        if (isAuthed) {
          try {
            const { data: c } = await axios.get(`${API}/chefs/${slug}/is-following`);
            setFollowing(!!c?.following);
          } catch {
            /* unauth fine */
          }
        }
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, isAuthed]);

  async function toggleFollow() {
    if (!isAuthed) {
      toast.info("Sign in to follow the chef and get recipe alerts.");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chefs/${slug}/follow`);
      setFollowing(!!data?.following);
      setChef((c) => (c ? { ...c, follower_count: data?.follower_count ?? c.follower_count } : c));
      toast.success(data?.following ? "Following — you'll get a 🔔 when a new recipe drops." : "Unfollowed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update follow.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="pt-32 text-center text-neutral-500">Loading…</div>
      </div>
    );
  }
  if (notFound || !chef) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <div className="pt-32 text-center">
          <div className="text-2xl mb-2">Chef not found.</div>
          <Link to="/" className="text-[#D4AF37] uppercase tracking-widest text-xs">← Home</Link>
        </div>
      </div>
    );
  }

  const t = chef?.egg?.theme || {};

  return (
    <div className="min-h-screen bg-black text-white" data-testid="chef-profile-page">
      <Header />

      {/* Hero — signature egg + name + follow */}
      <section
        className="relative pt-28 pb-12 overflow-hidden"
        style={{
          background: `radial-gradient(circle at 20% 30%, ${t.base || "#1E40AF"}50 0%, transparent 60%), #0A0A0A`,
        }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-8">
          {/* Signature egg + optional chef photo */}
          <div className="flex-shrink-0 relative">
            {chef.egg ? (
              <EggVisual
                theme={chef.egg.theme}
                letterGrade={chef.egg.letter_grade || "A★"}
                size={140}
                showRank
              />
            ) : (
              <div className="text-7xl">👨‍🍳</div>
            )}
            {chef.photo_url && (
              <img
                src={chef.photo_url}
                alt={chef.name}
                data-testid="chef-photo"
                className="absolute -bottom-3 -right-3 w-16 h-16 object-cover border-2 rounded-full bg-black"
                style={{ borderColor: t.ring || "#FCD34D" }}
                loading="lazy"
              />
            )}
          </div>
          <div className="flex-1 text-center md:text-left">
            <div
              className="text-[10px] uppercase tracking-[0.3em] font-bold"
              style={{ color: t.highlight || "#FCD34D" }}
            >
              {chef.title || "Chief Cook"} · OmniMuscle
            </div>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mt-1"
              data-testid="chef-name"
            >
              {chef.name}
            </h1>
            {chef.tagline && (
              <div className="text-base md:text-lg mt-2" style={{ color: t.highlight || "#FCD34D" }}>
                {chef.tagline}
              </div>
            )}
            <div className="mt-4 flex items-center gap-4 flex-wrap justify-center md:justify-start">
              <button
                onClick={toggleFollow}
                disabled={busy}
                data-testid="chef-follow-btn"
                className="font-bold uppercase tracking-[0.2em] text-xs py-3 px-6 transition-colors disabled:opacity-50"
                style={
                  following
                    ? { background: "transparent", color: "#FFFFFF", border: `2px solid ${t.ring || "#FCD34D"}` }
                    : { background: t.highlight || "#FCD34D", color: "#0A0A0A", border: `2px solid ${t.ring || "#FCD34D"}` }
                }
              >
                {busy ? "…" : following ? "✓ Following" : "Follow"}
              </button>
              <div className="text-xs text-neutral-400">
                <span data-testid="chef-follower-count" className="text-white font-bold">{chef.follower_count}</span>{" "}
                followers · <span className="text-white font-bold">{chef.recipe_count}</span> recipes
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bio */}
      {chef.bio && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-b border-zinc-900">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">
            About
          </div>
          <p className="text-neutral-200 leading-relaxed whitespace-pre-line" data-testid="chef-bio">
            {chef.bio}
          </p>
          {(chef.social?.instagram || chef.social?.tiktok || chef.social?.youtube) && (
            <SocialLinkRow
              source={{
                social_instagram: chef.social?.instagram,
                social_tiktok: chef.social?.tiktok,
                social_youtube: chef.social?.youtube,
              }}
              testIdPrefix="chef-social"
              className="mt-4"
            />
          )}
          {/* 💵 Tip the chef + Top Tippers leaderboard */}
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <TipJar recipient="david_lauer" recipientName={chef.name} />
            <TopTippers recipient="david_lauer" />
          </div>
        </section>
      )}

      {/* Recipes */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-4">
          Recipes by {chef.name}
        </div>
        {recipes.length === 0 ? (
          <div
            className="text-center py-16 border-2 border-dashed border-zinc-800 bg-[#0F0F0F] text-neutral-500"
            data-testid="chef-empty-recipes"
          >
            No recipes published yet — Follow to get the 🔔 when the first one drops.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="chef-recipes-list">
            {recipes.map((r) => (
              <Link
                key={r.recipe_id}
                to="/#food"
                data-testid={`chef-recipe-${r.recipe_id}`}
                className="bg-[#0F0F0F] border-2 border-zinc-900 hover:border-[#D4AF37] transition-colors p-4 block"
              >
                {r.hero_image && (
                  <div className="aspect-video bg-black border border-zinc-800 mb-3 overflow-hidden">
                    <img
                      src={r.hero_image}
                      alt={r.title}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="font-display text-xl text-white leading-tight">{r.title}</div>
                {r.tagline && (
                  <div className="text-xs text-neutral-400 mt-1 line-clamp-2">{r.tagline}</div>
                )}
                <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-3">
                  {r.prep_min ? `${r.prep_min}m prep` : ""}
                  {r.prep_min && r.cook_min ? " · " : ""}
                  {r.cook_min ? `${r.cook_min}m cook` : ""}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
